import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ClubDto } from "@tc/shared";
import { adminApi } from "@/features/clubs/api";

interface ClubsValue {
  clubs: ClubDto[];
  loading: boolean;
  /** Re-fetch the club list (after a create/edit so the switcher + page stay in sync). */
  refresh: () => Promise<void>;
}

const ClubsContext = createContext<ClubsValue | null>(null);

/**
 * Loads the cross-club list once for the Back Office shell (GET /api/clubs) and shares it
 * between the topbar ClubSwitcher and the clubs admin page, so creating a club updates both.
 * Mounted only inside BackOfficeLayout (the endpoint is BACK_OFFICE-only). Plain fetch, mirroring
 * AuthzProvider / NotificationsProvider — no TanStack Query yet.
 */
export function ClubsProvider({ children }: { children: ReactNode }) {
  const [clubs, setClubs] = useState<ClubDto[]>([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const list = await adminApi.listClubs();
      if (mounted.current) setClubs(list);
    } catch {
      /* transient — keep the last good list */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const value = useMemo<ClubsValue>(() => ({ clubs, loading, refresh }), [clubs, loading, refresh]);
  return <ClubsContext.Provider value={value}>{children}</ClubsContext.Provider>;
}

/** Access the back-office clubs list. Throws if used outside <ClubsProvider>. */
export function useClubs(): ClubsValue {
  const ctx = useContext(ClubsContext);
  if (!ctx) throw new Error("useClubs must be used within <ClubsProvider>");
  return ctx;
}

/**
 * A small palette giving each club a stable dot colour by its position in the list (tokens
 * only). Shared by the topbar switcher and the worklist surfaces so a club reads the same
 * colour everywhere.
 */
export const CLUB_DOT_COLORS = [
  "var(--primary)",
  "var(--info)",
  "var(--success)",
  "var(--warning)",
  "var(--slate)",
];

interface ClubLookup {
  /** Display name for a club id, or null if unknown / not provided. */
  name: (clubId: string | null | undefined) => string | null;
  /** Stable dot colour for a club id (falls back to a neutral token). */
  color: (clubId: string | null | undefined) => string;
}

/** Resolve club id → name + dot colour from the loaded back-office club list. */
export function useClubLookup(): ClubLookup {
  const { clubs } = useClubs();
  return useMemo<ClubLookup>(() => {
    const index = new Map(clubs.map((c, i) => [c.id, { name: c.name, i }]));
    return {
      name: (clubId) => (clubId ? (index.get(clubId)?.name ?? null) : null),
      color: (clubId) => {
        const entry = clubId ? index.get(clubId) : undefined;
        if (!entry) return "var(--slate)";
        return CLUB_DOT_COLORS[entry.i % CLUB_DOT_COLORS.length] ?? "var(--slate)";
      },
    };
  }, [clubs]);
}
